import type { CvParseQuality } from '@tabbio/types';

export interface CvImportReview {
  evidenceId: string;
  sourceFingerprint: string;
  resultFingerprint: string;
  writeFingerprint: string;
  targetKey: string;
  baseRevision: number;
  baseFingerprint: string;
}

export interface CvImportReviewRequired {
  review: CvImportReview;
  missingSections: string[];
  clearedSections: string[];
  quality: CvParseQuality | null;
}

export class CvImportReviewCancelledError extends Error {
  readonly code = 'CV_IMPORT_REVIEW_CANCELLED';
  constructor() {
    super('CV import review cancelled');
    this.name = 'CvImportReviewCancelledError';
  }
}

export function isCvImportReviewCancelled(error: unknown) {
  return (error as { code?: unknown } | null)?.code === 'CV_IMPORT_REVIEW_CANCELLED';
}

export function isCvImportSourceChanged(error: unknown) {
  return (error as { code?: unknown } | null)?.code === 'CV_IMPORT_SOURCE_CHANGED';
}

/** Validate the confirmation token before presenting a destructive review. */
export function getCvImportReviewRequired(error: unknown): CvImportReviewRequired | null {
  const candidate = error as { code?: unknown; meta?: Record<string, unknown> } | null;
  if (candidate?.code !== 'CV_IMPORT_REVIEW_REQUIRED') return null;
  const meta = candidate.meta;
  const review = meta?.review as CvImportReview | undefined;
  if (!review || typeof review.evidenceId !== 'string' ||
    !/^[0-9a-f-]{36}$/i.test(review.evidenceId) ||
    !['sourceFingerprint', 'resultFingerprint', 'writeFingerprint', 'baseFingerprint'].every(
      (key) => /^[0-9a-f]{64}$/i.test((review as unknown as Record<string, string>)[key] ?? ''),
    ) || typeof review.targetKey !== 'string' || !review.targetKey ||
    !Number.isSafeInteger(review.baseRevision) || review.baseRevision < 0 ||
    !Array.isArray(meta?.missingSections) || !meta.missingSections.every((section) => typeof section === 'string') ||
    !Array.isArray(meta?.clearedSections) || !meta.clearedSections.every((section) => typeof section === 'string')) return null;
  return {
    review,
    missingSections: meta.missingSections as string[],
    clearedSections: meta.clearedSections as string[],
    quality: meta.quality && typeof meta.quality === 'object' ? meta.quality as CvParseQuality : null,
  };
}

export async function runCvImportWithReview<P extends object, R>(
  payload: P,
  write: (payload: P & { importReview?: CvImportReview }) => Promise<R>,
  prompt: (required: CvImportReviewRequired) => Promise<boolean>,
): Promise<R> {
  // JSON requests are captured before any async work. Editing the screen while
  // the prompt is open cannot confirm a different write than the server saw.
  const captured = JSON.parse(JSON.stringify(payload)) as P;
  try {
    return await write(captured);
  } catch (error) {
    const required = getCvImportReviewRequired(error);
    if (!required) throw error;
    if (!(await prompt(required))) throw new CvImportReviewCancelledError();
    // One attempt only. A new review or changed source must return to editing.
    return write({ ...captured, importReview: required.review });
  }
}
